/**
 * WeeklyOrdersModal Component
 *
 * Dialog modal displaying all recurring orders organised by day of week.
 * Triggered from the Sales Record header bar.
 *
 * Extracted from WeeklyOrdersPage — follows PackingModal's self-fetching pattern.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { format, parseISO } from 'date-fns'
import { RefreshCcw, Pencil, Pause, Play, Plus } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { fetchRecurringOrders, pauseRecurringOrder, resumeRecurringOrder } from '@/services/recurringOrderService'
import EditRecurringOrderDialog from '@/components/EditRecurringOrderDialog'
import CreateRecurringOrderDialog from '@/components/customer/CreateRecurringOrderDialog'
import { CustomerDetailModal } from '@/components/customer'
import { fetchCustomer } from '@/supabase-client'

// Day configuration (ISO weekday: 1=Mon, 7=Sun)
const DAYS_OF_WEEK = [
  { number: 1, name: 'Monday', short: 'Mon' },
  { number: 2, name: 'Tuesday', short: 'Tue' },
  { number: 3, name: 'Wednesday', short: 'Wed' },
  { number: 4, name: 'Thursday', short: 'Thu' },
  { number: 5, name: 'Friday', short: 'Fri' },
  { number: 6, name: 'Saturday', short: 'Sat' },
  { number: 7, name: 'Sunday', short: 'Sun' }
]

function formatItemsList(items) {
  if (!items || items.length === 0) return []
  return items.map(item => {
    const productName = item.product?.strain || 'Unknown'
    const quantity = item.quantity
    return `${productName}: ${quantity}kg`
  })
}

function OrderRow({ order, onEdit, onPauseToggle, onCustomerClick, disabled }) {
  const isPaused = order.paused_until && new Date(order.paused_until) > new Date()
  const items = formatItemsList(order.items)
  const customerName = order.customer?.name || 'Unknown Customer'
  const branchName = order.branch?.name

  return (
    <div
      className="grid gap-4 px-6 py-3 text-sm border-b border-border/50 hover:bg-muted/30 transition-colors items-center"
      style={{ gridTemplateColumns: '25% 1fr auto auto' }}
    >
      <div className="min-w-0">
        <button
          onClick={() => onCustomerClick(order.customer)}
          className="font-medium hover:text-green-700 hover:underline text-left truncate block w-full"
        >
          {customerName}
        </button>
        {branchName && branchName.toLowerCase() !== 'main' && (
          <span className="text-sm opacity-70 truncate block">{branchName}</span>
        )}
      </div>

      <div className="min-w-0">
        {items.length > 0 ? (
          <div className="flex flex-col gap-0.5">
            {items.map((item, idx) => (
              <span key={idx} className="truncate">{item}</span>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground italic">No items</span>
        )}
      </div>

      <div className="flex items-center justify-end">
        <Badge
          variant={isPaused ? 'warning' : 'success'}
          className="text-xs whitespace-nowrap"
        >
          {isPaused
            ? `Paused until ${format(parseISO(order.paused_until), 'd MMM')}`
            : 'Active'
          }
        </Badge>
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPauseToggle(order)}
          disabled={disabled}
          className="h-8 w-8 p-0"
          title={isPaused ? 'Resume order' : 'Pause order'}
        >
          {isPaused ? (
            <Play className="h-4 w-4 text-green-600" />
          ) : (
            <Pause className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(order)}
          disabled={disabled}
          className="h-8 w-8 p-0"
          title="Edit order"
        >
          <Pencil className="h-4 w-4 text-muted-foreground" />
        </Button>
      </div>
    </div>
  )
}

function DaySection({ day, orders, onEdit, onPauseToggle, onCustomerClick, disabled }) {
  const hasOrders = orders.length > 0

  return (
    <div>
      <div className="sticky top-0 z-10 bg-white border-b px-6 py-1.5">
        <span className="text-xs font-semibold text-muted-foreground uppercase">
          {day.name}
        </span>
      </div>

      {hasOrders ? (
        orders.map((order) => (
          <OrderRow
            key={`${order.id}-${day.number}`}
            order={order}
            onEdit={onEdit}
            onPauseToggle={onPauseToggle}
            onCustomerClick={onCustomerClick}
            disabled={disabled}
          />
        ))
      ) : (
        <div className="py-4 px-6 text-sm text-muted-foreground italic border-b border-border/50">
          No recurring orders
        </div>
      )}
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      {DAYS_OF_WEEK.slice(0, 5).map((day) => (
        <div key={day.number} className="space-y-2">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-32 w-full rounded-lg" />
        </div>
      ))}
    </div>
  )
}

export default function WeeklyOrdersModal({ isOpen, onClose }) {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [customerModalOpen, setCustomerModalOpen] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [createOrderDialogOpen, setCreateOrderDialogOpen] = useState(false)

  const loadOrders = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchRecurringOrders({ activeOnly: true })
      setOrders(data)
    } catch (err) {
      console.error('[WeeklyOrdersModal] Error loading orders:', err)
      setError('Failed to load recurring orders')
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch data when modal opens (PackingModal pattern)
  useEffect(() => {
    if (!isOpen) return
    loadOrders()
  }, [isOpen, loadOrders])

  const ordersByDay = useMemo(() => {
    const grouped = {}
    DAYS_OF_WEEK.forEach(day => { grouped[day.number] = [] })

    orders.forEach(order => {
      const days = order.days_of_week || []
      days.forEach(dayNum => {
        if (grouped[dayNum]) grouped[dayNum].push(order)
      })
    })

    Object.keys(grouped).forEach(dayNum => {
      grouped[dayNum].sort((a, b) => {
        const nameA = a.customer?.name || ''
        const nameB = b.customer?.name || ''
        return nameA.localeCompare(nameB)
      })
    })

    return grouped
  }, [orders])

  const handleEdit = useCallback((order) => {
    setSelectedOrder(order)
    setEditDialogOpen(true)
  }, [])

  const handlePauseToggle = useCallback(async (order) => {
    const isPaused = order.paused_until && new Date(order.paused_until) > new Date()
    setActionLoading(true)
    try {
      if (isPaused) {
        await resumeRecurringOrder(order.id)
        toast.success('Order resumed', { description: 'Deliveries will continue as scheduled' })
      } else {
        const twoWeeksFromNow = new Date()
        twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14)
        const dateStr = format(twoWeeksFromNow, 'yyyy-MM-dd')
        await pauseRecurringOrder(order.id, dateStr)
        toast.success('Order paused', {
          description: `Deliveries paused until ${format(twoWeeksFromNow, 'd MMM yyyy')}`
        })
      }
      await loadOrders()
    } catch (err) {
      console.error('[WeeklyOrdersModal] Error toggling pause:', err)
      toast.error(`Failed to ${isPaused ? 'resume' : 'pause'} order`)
    } finally {
      setActionLoading(false)
    }
  }, [loadOrders])

  const handleCustomerClick = useCallback(async (customer) => {
    if (customer?.id) {
      try {
        const fullCustomer = await fetchCustomer(customer.id)
        setSelectedCustomer({ ...fullCustomer, companyName: fullCustomer.name })
        setCustomerModalOpen(true)
      } catch (err) {
        console.error('[WeeklyOrdersModal] Error fetching customer:', err)
        toast.error('Failed to load customer details')
      }
    }
  }, [])

  const handleEditClose = useCallback(() => {
    setEditDialogOpen(false)
    setSelectedOrder(null)
  }, [])

  const handleEditSave = useCallback(async () => {
    setEditDialogOpen(false)
    setSelectedOrder(null)
    toast.success('Recurring order updated')
    await loadOrders()
  }, [loadOrders])

  const handleCustomerModalClose = useCallback(() => {
    setCustomerModalOpen(false)
    setSelectedCustomer(null)
  }, [])

  const handleOrderCreated = useCallback(() => {
    setCreateOrderDialogOpen(false)
    loadOrders()
  }, [loadOrders])

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
        <DialogContent className="odin-modal-large" showCloseButton={false}>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Weekly Orders</DialogTitle>
                <DialogDescription>
                  Recurring orders organised by day of week
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setCreateOrderDialogOpen(true)}
                  className="gap-1.5"
                >
                  <Plus className="h-4 w-4" />
                  Add Recurring Order
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadOrders}
                  disabled={loading}
                  className="gap-1.5"
                >
                  <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="mt-2">
            {loading ? (
              <LoadingSkeleton />
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600 mb-4">{error}</p>
                <Button variant="outline" onClick={loadOrders} className="gap-2">
                  <RefreshCcw className="h-4 w-4" />
                  Retry
                </Button>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                <RefreshCcw className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">No recurring orders</h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto">
                  Create a recurring order from the Sales page by checking &quot;Make this a recurring order&quot; when creating a sale.
                </p>
              </div>
            ) : (
              <div className="border border-border rounded-lg overflow-hidden">
                {DAYS_OF_WEEK.map((day) => (
                  <DaySection
                    key={day.number}
                    day={day}
                    orders={ordersByDay[day.number] || []}
                    onEdit={handleEdit}
                    onPauseToggle={handlePauseToggle}
                    onCustomerClick={handleCustomerClick}
                    disabled={actionLoading}
                  />
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Recurring Order Dialog */}
      {selectedOrder && (
        <EditRecurringOrderDialog
          order={selectedOrder}
          customerId={selectedOrder.customer_id}
          open={editDialogOpen}
          onOpenChange={(open) => { if (!open) handleEditClose() }}
          onSave={handleEditSave}
        />
      )}

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <CustomerDetailModal
          customer={selectedCustomer}
          open={customerModalOpen}
          onOpenChange={(open) => { if (!open) handleCustomerModalClose() }}
          onSave={async () => { await loadOrders() }}
        />
      )}

      {/* Create Recurring Order Dialog */}
      <CreateRecurringOrderDialog
        open={createOrderDialogOpen}
        onOpenChange={setCreateOrderDialogOpen}
        onCreated={handleOrderCreated}
      />
    </>
  )
}
